import { GraphicObject } from "../graphic-object";

export class WebglObject extends GraphicObject {
  constructor(settings) {
    super(settings);
    if (this.settings.debug.boxFillColor === undefined) {
      this.settings.debug.boxFillColor = [0, 0, 0, 0];
    }
    if (this.settings.debug.boxStrokeColor === undefined) {
      this.settings.debug.boxStrokeColor = [1, 0, 0, 1];
    }
    if (this.settings.isVisible === undefined) {
      this.settings.isVisible = true;
    }
  }

  setVisibility(isVisible) {
    this.settings.isVisible = isVisible;
  }

  setDebugMode(debugSettings) {
    super.setDebugMode(debugSettings);

    if (this.settings.debug.fillWebglElements || this.settings.debug.strokeWebglElements) {
      const gl = this.settings.gl;
      this.uniforms = {
        u_color: new Float32Array(this.settings.debug.boxFillColor),
        u_shift: new Float32Array([0.0, 0.0]),
        u_rollIndex: 0,
        u_scale: new Float32Array([1, 1]),
        u_windowSize: new Float32Array([this.settings.canvas.settings.sizeInPixels.width, this.settings.canvas.settings.sizeInPixels.height])
      };
      if (debugSettings.fillWebglElements) {
        this.settings.debug.boxFillBuffer = WebglObject.createRectangleFillBuffer(this.settings.absoluteLocation, this.settings.sizeInPixels);
        this.settings.debug.boxFillIndices = new Uint16Array([3, 2, 1, 3, 1, 0]);

        this.settings.debug.boxFillVerticesBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.settings.debug.boxFillVerticesBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, this.settings.debug.boxFillBuffer, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);

        this.settings.debug.boxFillIndicesBuffer = this.gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.settings.debug.boxFillIndicesBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.settings.debug.boxFillIndices, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
      }
      if (debugSettings.strokeWebglElements) {
        this.settings.debug.boxStrokeBuffer = WebglObject.createRectangleStrokeBuffer(this.settings.absoluteLocation, this.settings.sizeInPixels, 1);
        this.settings.debug.boxStrokeVerticesBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.settings.debug.boxStrokeVerticesBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, this.settings.debug.boxStrokeBuffer, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
      }
    }
  }

  static createRectangleFillBuffer(location, size) {
    return new Float32Array([
      location.x,
      location.y + size.height,
      location.x,
      location.y,
      location.x + size.width,
      location.y,
      location.x + size.width,
      location.y + size.height
    ]);
  }

  static createRectangleStrokeBuffer(location, size, shift = 0) {
    return new Float32Array([
      location.x + shift,
      location.y + size.height - 2 * shift,
      location.x + shift - 2 * shift,
      location.y + shift,
      location.x + shift,
      location.y + shift,
      location.x + size.width - 2 * shift,
      location.y + shift,
      location.x + size.width - 2 * shift,
      location.y + shift,
      location.x + size.width - 2 * shift,
      location.y + size.height - 2 * shift,
      location.x + size.width - 2 * shift,
      location.y + size.height - 2 * shift,
      location.x + shift,
      location.y + size.height - 2 * shift
    ]);
  }

  updateVertexBuffers() {
    if (!this.settings.isDebugMode) {
      return;
    }
    this.uniforms.u_windowSize = new Float32Array([this.settings.canvas.settings.sizeInPixels.width, this.settings.canvas.settings.sizeInPixels.height]);
    const gl = this.settings.gl;
    if (this.settings.debug.fillWebglElements) {
      this.settings.debug.boxFillBuffer = WebglObject.createRectangleFillBuffer(this.settings.absoluteLocation, this.settings.sizeInPixels);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.settings.debug.boxFillVerticesBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.settings.debug.boxFillBuffer, gl.STATIC_DRAW);
    }

    if (this.settings.debug.strokeWebglElements) {
      this.settings.debug.boxStrokeBuffer = WebglObject.createRectangleStrokeBuffer(this.settings.absoluteLocation, this.settings.sizeInPixels, 1);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.settings.debug.boxStrokeVerticesBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.settings.debug.boxStrokeBuffer, gl.STATIC_DRAW);
    }
  }

  createBorders() {
    super.createBorders();
    if (this.settings.isDebugMode) {
      this.updateVertexBuffers();
    }
  }

  draw(gl, program) {
    if (!this.settings.isVisible || !this.settings.isDebugMode || (!this.settings.debug.fillWebglElements && !this.settings.debug.strokeWebglElements)) {
      return;
    }
    gl.viewport(0, 0, this.uniforms.u_windowSize[0], this.uniforms.u_windowSize[1]);
    if (
      this.settings.debug.fillWebglElements &&
      (this.settings.debug.isTransparentBackground === undefined || !this.settings.debug.isTransparentBackground)
    ) {
      this.uniforms.u_color = new Float32Array(this.settings.debug.boxFillColor);
      program.setUnforms(this.uniforms);
      gl.enableVertexAttribArray(program.attribs.a_position);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.settings.debug.boxFillVerticesBuffer);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.settings.debug.boxFillIndicesBuffer);
      gl.vertexAttribPointer(program.attribs.a_position, 2, gl.FLOAT, false, 0, 0);
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
    } else if (this.settings.debug.strokeWebglElements) {
      gl.enableVertexAttribArray(program.attribs.a_position);
    }
    if (this.settings.debug.strokeWebglElements) {
      this.uniforms.u_color = new Float32Array(this.settings.debug.boxStrokeColor);
      program.setUnforms(this.uniforms);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.settings.debug.boxStrokeVerticesBuffer);
      gl.vertexAttribPointer(program.attribs.a_position, 2, gl.FLOAT, false, 0, 0);
      gl.drawArrays(gl.LINES, 0, this.settings.debug.boxStrokeBuffer.length / 2);
    }
  }
}
